import React, { useEffect, useRef, useState } from 'react';
import withAuthContext from '../../context/auth/AuthConsumer';
import { authFetch } from '../../services/authClient';
import { IAuthContext } from '../../context/auth/auth-context';
import EnviroChemicalAISidebar from './Sidebar';
import '../enviro-deep-research/index.scss';
import EnviroChemicalAIInput from './EnviroChemicalAIInput';
import Logo from '../../assets/header-enviroai-logo.svg';
import TopRightNav from '../enviro-deep-research/TopRightNav';
import ChatWindow from '../enviro-deep-research/ChatWindow';
import { ChatResponse } from '../../types/deep-research';

// For now reuse the same API until a dedicated enviro-chemical-ai endpoint is available
const baseApiUrl = `${process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT}/research/get_pred`;

const EnviroChemicalAI: React.FC<{ context: IAuthContext }> = ({ context }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [chatData, setChatData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState<number | null>(null);
  const [isResearchStarted, setIsResearchStarted] = useState(false);
  const interval = useRef<number | null>(null);
  const textareaRef: any = useRef(null);

  const fetchResponse = async (manualSearchText?: string) => {
    const localSearchText = searchText.trim() || manualSearchText?.trim() || '';
    const keepAlive = isResearchStarted;
    if (!keepAlive && localSearchText.trim() === '') return;

    if (!keepAlive) {
      const userMessage = {
        content: localSearchText,
        role: 'user',
      };
      setChatData((prev) => [...prev, userMessage]);
      setSearchText('');
      textareaRef.current.style.height = '34px';
    }
    setIsLoading(true);

    try {
      const chatStatus = chatId || keepAlive ? 'ongoing' : 'new';
      const q = encodeURIComponent(keepAlive ? 'keep alive' : localSearchText);
      const localUserId = localStorage.getItem('uid');
      const userId = localUserId ? encodeURIComponent(localUserId) : '';
      const cancelToken = new AbortController();

      const queryParams = new URLSearchParams({
        chatStatus,
        q,
      });

      if (userId) {
        queryParams.append('userId', userId);
      }
      if (chatId) {
        queryParams.append('chatId', chatId.toString());
      }

      if(chatStatus === 'ongoing' && !isResearchStarted) {
        setIsResearchStarted(true);
      }

      const url = `${baseApiUrl}?${queryParams.toString()}`;
      const fetchOptions: any = {
        method: 'GET',
        signal: cancelToken.signal,
      };
      const response = await authFetch(url, fetchOptions);
      const data = await response.json() as ChatResponse;
      if(!data) {
        return;
      }
      setChatId(typeof data.chat_id === 'string' ? parseInt(data.chat_id, 10) : data.chat_id);
      const assistantMessage = {
        content: data.content || 'No response received.',
        status: data.status,
        pdf: data.pdf,
      };

      setChatData((prev) => {
        const existingItem = prev.find((item) => item.status === 'executing');
        if(!existingItem) {
          return [...prev, assistantMessage];
        }
        existingItem.content = assistantMessage.content;
        existingItem.pdf = assistantMessage.pdf;
        return prev;
      });
      if(data.status === 'completed') {
        setIsResearchStarted(false);
        if(interval.current) {
          window.clearInterval(interval.current);
        }
      }
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log('Error fetching response:', error);
    } finally {
      setIsLoading(false);
    }

    setSearchText('');
  };

  useEffect(() => {
    if (chatId && isResearchStarted) {
      setIsLoading(true);
      const id = window.setInterval(() => {
        fetchResponse();
      }, 30000);
      interval.current = id;
      return () => {
        if (interval.current) {
          window.clearInterval(interval.current);
        }
      };
    }
  // eslint-disable-next-line
  }, [chatId, isResearchStarted]);

  return (
    <div className="enviro-deep-research">
      <EnviroChemicalAISidebar isHovered={isHovered} setIsHovered={setIsHovered} />
      <div className="search-area-container cmn-chem-main-bg">
        <TopRightNav />
        {chatData.length ? (
          <ChatWindow
            chatData={chatData}
            isLoading={isLoading || isResearchStarted}
            onStartResearch={() => fetchResponse('Please go ahead')}
          />
        ) : (
          <>
            <img src={Logo} alt="EnviroAI" className="enviro-ai-logo" />
            <p className="enviro-subtitle"><span className="blue-text">Enviro</span>Chemical AI pro 1.0</p>
          </>
        )}
        <EnviroChemicalAIInput
          value={searchText}
          onChange={(e: any) => setSearchText(e.target.value)}
          onEnter={() => fetchResponse()}
          textareaRef={textareaRef} />
      </div>
    </div>
  );
};

export default withAuthContext(EnviroChemicalAI);

